'use client'

import { styled } from 'styled-components'

import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import { ButtonAsA, FormattedMessage } from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'

import type { FeaturedAuctionsData } from '@/helpers/cms/contentTypes'

const FeaturedLiquidationContainer = styled.section`
  display: flex;
  justify-content: center;
`

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 3rem 1.5rem;
  max-width: var(--maxPageWidth);
  width: 100%;
  @media ${Breakpoints.min.medium} {
    padding: 4rem 3rem;
  }
`

const FeaturedLiquidationTitle = styled.h3`
  ${Typography.Title2}
  margin-bottom: 1rem;
  color: ${Colors.Semantic.Neutral.light_black};
`

const FeaturedLiquidationContent = styled.p`
  ${Typography.Body1}
  margin-bottom: 1.5rem;
  color: ${Colors.Semantic.Neutral.dark_grey};
`

const StyledButton = styled(ButtonAsA)`
  width: fit-content;
  max-width: fit-content;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  white-space: nowrap;
`

const FeaturedLiquidationAuctions = ({
  data,
}: {
  /** CMS copy when the marketplace-content flag is on and this section is filled in. */
  data?: FeaturedAuctionsData | null
}) => {
  const isAuthenticated = useDecodedAccessToken() !== null

  if (isAuthenticated) {
    return null
  }

  // Field by field rather than all-or-nothing: an editor who changes only the heading should not
  // have to retype the 388-character SEO blurb to keep it.
  return (
    <FeaturedLiquidationContainer data-testid="featured-liquidation-auctions">
      <InnerWrapper>
        <FeaturedLiquidationTitle>
          {data?.title ?? (
            <FormattedMessage id="Marketing.featuredLiquidationAuctions.title" />
          )}
        </FeaturedLiquidationTitle>
        <FeaturedLiquidationContent>
          {data?.description ?? (
            <FormattedMessage id="Marketing.featuredLiquidationAuctions.description" />
          )}
        </FeaturedLiquidationContent>
        <StyledButton
          appearance="primary"
          size="medium"
          href={data?.ctaUrl ?? '/all-auctions/'}
          target="_blank"
          rel="noopener"
        >
          {data?.ctaLabel ?? (
            <FormattedMessage id="Marketing.featuredLiquidationAuctions.CTA" />
          )}
        </StyledButton>
      </InnerWrapper>
    </FeaturedLiquidationContainer>
  )
}

export default FeaturedLiquidationAuctions
